import {
  Component,
  effect,
  ElementRef,
  input,
  ViewChild,
  AfterViewInit,
  inject,
} from '@angular/core';
import { ChartComponent } from '../chart/chart.component';
import { NgStyle } from '@angular/common';
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  ChartConfiguration,
  Filler,
} from 'chart.js';
import { NgIcon } from '@ng-icons/core';
import { DictionaryService } from '../../services/dictionary.service';
import { LoadingSpinnerComponent } from "../loading-spinner/loading-spinner.component";

export interface RadarChart {
  [key: string]: number;
}

@Component({
  selector: 'app-radar-chart',
  imports: [NgStyle, NgIcon, LoadingSpinnerComponent],
  templateUrl: './radar-chart.component.html',
  styleUrl: './radar-chart.component.scss',
})
export class RadarChartComponent
  extends ChartComponent
  implements AfterViewInit {
  // Inputs
  data = input<RadarChart>({});
  chartTitle = input<string>('chart');
  dictionaryService = inject(DictionaryService);

  // View Child
  @ViewChild('radarchartcanvas', { static: true }) public chartElm:
    | ElementRef
    | undefined;

  constructor() {
    super();
    this.chartType = 'radar';

    // 1. Register CORRECT modules for Radar Chart
    Chart.register(
      RadarController,
      RadialLinearScale,
      PointElement,
      LineElement,
      Filler
    );

    // 2. Use Effect to react to Signal Input changes automatically
    effect(() => {
      const currentData = this.data();
      // Only update if chart exists and we have data
      if(this.chart){
        if (Object.keys(currentData).length > 0) {
          this.updateChartData(currentData);
        }
      }

    });
  }

  // 3. Initialize Chart here, when the Canvas element is guaranteed to exist
  ngAfterViewInit(): void {
    if (!this.chartElm) return;
    this.createChart();
  }

  private createChart() {
    if (!this.chartElm) return;

    const config: ChartConfiguration<'radar'> = {
      type: 'radar',
      data: {
        labels: Object.keys(this.data()),
        datasets: [
          {
            label: this.chartTitle(),
            data: Object.values(this.data()),
            backgroundColor: '#2bb5cf6b',
            borderColor: '#1da2b9',
            pointBackgroundColor: '#1da2b9',
            pointBorderColor: '#fff',
            fill: true
          },
        ],
      },
      options: this.getOptions(), // Extracted options for cleanliness
    };

    // Create the Chart Instance
    this.chart = new Chart(this.chartElm.nativeElement, config);
  }

  private updateChartData(data: RadarChart) {
    if (!this.chart) return;
    this.chart.data.labels = Object.keys(data);
    this.chart.data.datasets[0].data = Object.values(data);
    this.chart.data.datasets[0].label = this.chartTitle();
    this.chart.update();
  }

  private getOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          angleLines: { display: true },
          suggestedMin: 0,
          suggestedMax: 5,
          ticks: { maxTicksLimit: 7 },
        },
      },
      plugins: {
        datalabels: { display: false },
        legend: { display: false },
        title: {
          text: this.chartTitle(),
          display: true,
          position: 'bottom' as const,
          color: 'hsl(198, 66%, 15%)',
        },
      },
    };
  }

  // Abstract implementations
  override setEmptyOptions(): void { }
  override setLoadingOptions(): void { }
  override setDataOptions(): void {
    if (this.chart) {
      this.chart.options = this.getOptions();
      this.chart.update();
    }
  }

  override isDataAvailable = () => {
    return this.data() && Object.keys(this.data()).length > 0;
  };
}
